import {
  VictoryBar,
  VictoryLine,
  VictoryChart,
  VictoryAxis,
  VictoryTheme,
  VictoryLabel,
} from "victory";

import { useTranslation } from "react-i18next";
import { ReactComponent as BarChart } from "./svgs/barchart.svg";
import {
  getMaxValueInDataSet,
  getHumanFriendlyGraphIntervals,
} from "../../../../functions/graphUtilities";
import style from "./CanisterUtilizationGraph.module.css";
import "./CanisterUtilizationGraph.css";

const CanisterUtilizationGraph = ({
  user,
  fetchingData,
  graphData,
  graphTitle,
  palette,
  primarySeriesLabel,
  secondarySeriesLabel,
}) => {
  // For Testing
  // const graphData = [
  //   { x: `canister 1\nid-1`, y: 200, y1: 12 },
  //   { x: `canister 2\nid-1`, y: 277, y1: 16 },
  //   { x: `canister 3\nid-1`, y: 1770, y1: 11 },
  // ];

  const { t } = useTranslation("graphsGeneral");

  const ticks = 5;

  // when ticks = 5, returns [0, 1, 2, 3, 4, 5]
  // or, using the npm package lodash, the same as const tickValues = _.range(6);
  const tickValues = Array.from({ length: ticks + 1 }, (_, i) => i);

  // function getMaxValueInDataSet(dataSet) {
  //   let maxValue = -1;
  //   dataSet.forEach((num) => {
  //     if (num > maxValue) {
  //       maxValue = num;
  //     }
  //   });
  //   return Math.ceil(maxValue / ticks) * ticks;
  // }

  const maxYValueNumOfScripts = getMaxValueInDataSet(
    graphData.map((dataObj) => dataObj.y),
    ticks
  );
  const maxYValueAvgScripts = getMaxValueInDataSet(
    graphData.map((dataObj) => dataObj.y1),
    ticks
  );

  let maxYValue = maxYValueNumOfScripts;

  if (maxYValueAvgScripts > maxYValue) {
    maxYValue = maxYValueAvgScripts;
  }

  let yRange = [0, ticks];

  if (maxYValue > ticks) {
    yRange = [0, getHumanFriendlyGraphIntervals(maxYValue, ticks)[ticks]];
  }

  const domain = { y: [0, ticks] };

  const tickFormatBar = (range) => (t) =>
    ((t * (range[1] - range[0])) / ticks).toFixed(1);

  const tickFormatLine = (range) => (t) =>
    `$${((t * (range[1] - range[0])) / ticks).toFixed(2)}`;

  const normalize = (range, props) => (datum) =>
    datum[props] / ((range[1] - range[0]) / ticks);

  let barData = [];
  let lineData = [];

  const styles = getStyles(barData);

  const color = palette[0];

  const Legend = (
    <div className={style.CanisterUtilizationGraph__legendContainer}>
      <div className={style.CanisterUtilizationGraph__legendGroup}>
        <BarChart />
        <p>{primarySeriesLabel}</p>
      </div>
      <div className={style.CanisterUtilizationGraph__legendGroup}>
        <div className={style.CanisterUtilizationGraph__line}></div>
        <p>{secondarySeriesLabel}</p>
      </div>
    </div>
  );

  graphData.forEach((dataObj, index) => {
    barData.push({
      name: dataObj.x,
      amount: dataObj.y,
    });
    lineData.push({
      x: index + 1,
      y: dataObj.y1,
    });
  });

  let xValueNoGraphDataFound = 176;
  let yValueNoGraphDataFound = 132;

  if (user?.language?.type === "es") {
    xValueNoGraphDataFound = 138;
  } else if (user?.language?.type === "fr") {
    xValueNoGraphDataFound = 140;
  }

  return (
    <div className={style.CanisterUtilizationGraph__container}>
      <h2>{graphTitle}</h2>
      <VictoryChart
        domain={domain}
        domainPadding={{ x: 26, y: 0 }}
        theme={VictoryTheme.material}
        width={450}
        height={266}
      >
        {!barData.length && (
          <VictoryLabel
            x={xValueNoGraphDataFound}
            y={yValueNoGraphDataFound}
            style={{
              fontFamily: "inherit",
              fontStyle: "normal",
              fontSize: "14px",
              fontWeight: "normal",
              fill: "	#909090",
            }}
            text={fetchingData ? "" : t("noData")}
          />
        )}

        <VictoryLabel
          angle={-90}
          x={4}
          y={172}
          text={primarySeriesLabel}
          style={styles.dependentAxisLabel}
        />

        {/* independent x axis */}
        <VictoryAxis
          style={{
            axis: { stroke: "#909090", strokeWidth: 1 },
            ticks: { stroke: "#909090", strokeWidth: 1 },
            tickLabels: {
              fill: !fetchingData && barData.length ? "#455A64" : "transparent",
            },
            grid: {
              stroke: "transparent",
            },
          }}
        />
        <VictoryAxis
          crossAxis={false}
          tickValues={tickValues}
          tickFormat={tickFormatBar(yRange)}
          dependentAxis
          style={{
            axis: { stroke: "#909090", strokeWidth: 1 },
            ticks: { stroke: "#909090", strokeWidth: 1 },
            tickLabels: {
              fill: !fetchingData && barData.length ? "#455A64" : "transparent",
            },
            grid: {
              stroke: "#CFD8DC",
              strokeDasharray: "0, 0",
            },
          }}
        />
        <VictoryBar
          data={barData}
          y={normalize(yRange, "amount")}
          barRatio={1}
          style={{ data: { fill: color } }}
          x={(d) => d.name}
          // y="amount"
          barWidth={barData.length > 1 ? null : 120}
        />
        <VictoryLine
          data={lineData}
          y={normalize(yRange, "y")}
          style={{
            data: {
              stroke: "#e32636",
              strokeWidth: 3,
            },
          }}
        />
      </VictoryChart>
      {Legend}
    </div>
  );
};

function getStyles(barData) {
  return {
    dependentAxisLabel: {
      fontFamily: "Segoe UI",
      fill: barData.length ? "#424242" : "transparent",
    },
  };
}

export default CanisterUtilizationGraph;
